import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Collapse,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import CourseService from "../../services/CourseService";
import { toast } from "react-toastify";

interface Props {
  courseId?: any;
}

export default function CourseDetails({ courseId }: Props) {
  const [areas, setAreas] = useState<any[]>([]);
  const [openAreaId, setOpenAreaId] = useState<number | null>(null);
  const [openSubareaIds, setOpenSubareaIds] = useState<number[]>([]);

  const [loadingAreas, setLoadingAreas] = useState(false);
  const [loadingSubareas, setLoadingSubareas] = useState<Record<string, boolean>>({});
  const [loadingSpecialties, setLoadingSpecialties] = useState<Record<string, boolean>>({});
  const [actionLoading, setActionLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"area" | "subarea" | "specialty">("area");
  const [editing, setEditing] = useState<any | null>(null);
  const [nameValue, setNameValue] = useState("");
  const [parentId, setParentId] = useState<any>(null);

  // Load top-level areas for the course
  const loadAreas = () => {
    if (!courseId) return;
    setLoadingAreas(true);
    CourseService.getAreas(courseId)
      .then((res: any) => setAreas(res?.data || []))
      .catch(() => setAreas([]))
      .finally(() => setLoadingAreas(false));
  };

  useEffect(() => {
    loadAreas();
  }, [courseId]);

  // Fetch subareas on demand for an area
  const fetchSubareas = (areaId: number) => {
    setLoadingSubareas((s) => ({ ...s, [String(areaId)]: true }));
    CourseService.getSubareas(areaId)
      .then((res: any) => {
        setAreas((prev) => prev.map((a) => (a.id === areaId ? { ...a, subareas: res?.data || [] } : a)));
      })
      .catch(() => {})
      .finally(() => setLoadingSubareas((s) => ({ ...s, [String(areaId)]: false })));
  };

  // Fetch specialties on demand for a subarea
  const fetchSpecialties = (subAreaId: number) => {
    setLoadingSpecialties((s) => ({ ...s, [String(subAreaId)]: true }));
    CourseService.getSpecialties(subAreaId)
      .then((res: any) => {
        const specialties = res?.data || [];
        setAreas((prev) =>
          prev.map((a) => ({
            ...a,
            subareas: (a.subareas || []).map((sub: any) => (sub.id === subAreaId ? { ...sub, specialties } : sub)),
          }))
        );
      })
      .catch(() => {})
      .finally(() => setLoadingSpecialties((s) => ({ ...s, [String(subAreaId)]: false })));
  };

  const handleToggleArea = (id: number) => {
    const next = openAreaId === id ? null : id;
    setOpenAreaId(next);
    if (next !== null) {
      const area = areas.find((a) => a.id === id);
      if (!area?.subareas || area.subareas.length === 0) fetchSubareas(id);
    }
  };

  const handleToggleSubarea = (subId: number) => {
    const isOpen = openSubareaIds.includes(subId);
    if (isOpen) return setOpenSubareaIds((prev) => prev.filter((i) => i !== subId));
    setOpenSubareaIds((prev) => [...prev, subId]);
    const sub = areas.flatMap((a) => a.subareas || []).find((s) => s.id === subId);
    if (sub && (!sub.specialties || sub.specialties.length === 0)) fetchSpecialties(subId);
  };

  const openCreate = (mode: "area" | "subarea" | "specialty", pId?: any) => {
    setDialogMode(mode);
    setParentId(pId || null);
    setEditing(null);
    setNameValue("");
    setDialogOpen(true);
  };

  const openEdit = (mode: "area" | "subarea" | "specialty", item: any) => {
    setDialogMode(mode);
    setEditing(item);
    setParentId(null);
    setNameValue(item.name || "");
    setDialogOpen(true);
  };

  const handleDeleteArea = (id: any) => {
    if (!window.confirm("Confirmar exclusão?")) return;
    setActionLoading(true);
    CourseService.removeArea(id)
      .then(() => {
        toast("Área excluída", { type: "success" });
        loadAreas();
      })
      .catch(() => toast("Erro ao excluir", { type: "error" }))
      .finally(() => setActionLoading(false));
  };

  const handleDeleteSubarea = (id: any) => {
    if (!window.confirm("Confirmar exclusão?")) return;
    setActionLoading(true);
    CourseService.removeSubarea(id)
      .then(() => {
        toast("Subárea excluída", { type: "success" });
        loadAreas();
      })
      .catch(() => toast("Erro ao excluir", { type: "error" }))
      .finally(() => setActionLoading(false));
  };

  const handleDeleteSpecialty = (id: any) => {
    if (!window.confirm("Confirmar exclusão?")) return;
    setActionLoading(true);
    CourseService.removeSpecialty(id)
      .then(() => {
        toast("Especialidade excluída", { type: "success" });
        loadAreas();
      })
      .catch(() => toast("Erro ao excluir", { type: "error" }))
      .finally(() => setActionLoading(false));
  };

  const submitDialog = () => {
    if (!nameValue) return toast("Informe o nome", { type: "error" });
    setActionLoading(true);

    const finish = (msg?: string) => {
      if (msg) toast(msg, { type: "success" });
      setActionLoading(false);
      setDialogOpen(false);
      loadAreas();
    };

    if (dialogMode === "area") {
      const op = editing ? CourseService.updateArea(editing.id, { name: nameValue }) : CourseService.storeArea(courseId, { name: nameValue });
      op.then(() => finish(editing ? "Área atualizada" : "Área criada")).catch(() => toast("Erro", { type: "error" })).finally(() => setActionLoading(false));
      return;
    }

    if (dialogMode === "subarea") {
      const op = editing ? CourseService.updateSubarea(editing.id, { name: nameValue }) : CourseService.storeSubarea(parentId, { name: nameValue });
      op.then(() => finish(editing ? "Subárea atualizada" : "Subárea criada")).catch(() => toast("Erro", { type: "error" })).finally(() => setActionLoading(false));
      return;
    }

    if (dialogMode === "specialty") {
      const op = editing ? CourseService.updateSpecialty(editing.id, { name: nameValue }) : CourseService.storeSpecialty(parentId, { name: nameValue });
      op.then(() => finish(editing ? "Especialidade atualizada" : "Especialidade criada")).catch(() => toast("Erro", { type: "error" })).finally(() => setActionLoading(false));
      return;
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Áreas</Typography>
        <Button startIcon={<AddIcon />} variant="outlined" onClick={() => openCreate("area")} disabled={actionLoading}>
          Nova Área
        </Button>
      </Box>

      {loadingAreas ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <List>
          {areas.map((area) => (
            <Box key={area.id}>
              <ListItem>
                <ListItemText
                  primary={
                    <Box display="flex" sx={{ minWidth: "400px" }} alignItems="center">
                      <Box mr={1}>{area.name}</Box>
                      {loadingSubareas[String(area.id)] && <CircularProgress size={14} />}
                    </Box>
                  }
                />
                <ListItemSecondaryAction sx={{ float: "right" }}>
                  <IconButton edge="end" onClick={() => openCreate("subarea", area.id)} size="large" disabled={actionLoading}>
                    <AddIcon />
                  </IconButton>
                  <IconButton edge="end" onClick={() => openEdit("area", area)} size="large" disabled={actionLoading}>
                    <EditIcon />
                  </IconButton>
                  <IconButton edge="end" onClick={() => handleDeleteArea(area.id)} size="large" disabled={actionLoading}>
                    <DeleteIcon />
                  </IconButton>
                  <IconButton edge="end" onClick={() => handleToggleArea(area.id)} size="large">
                    {openAreaId === area.id ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>

              <Collapse in={openAreaId === area.id} timeout="auto" unmountOnExit>
                <Box pl={4}>
                  <Typography variant="subtitle1">Subáreas</Typography>
                  <List>
                    {(area.subareas || []).map((sub: any) => (
                      <Box key={sub.id}>
                        <ListItem>
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center">
                                <Box mr={1}>{sub.name}</Box>
                                {loadingSpecialties[String(sub.id)] && <CircularProgress size={12} />}
                              </Box>
                            }
                          />
                          <ListItemSecondaryAction>
                            <IconButton edge="end" onClick={() => openCreate("specialty", sub.id)} size="large" disabled={actionLoading}>
                              <AddIcon />
                            </IconButton>
                            <IconButton edge="end" onClick={() => openEdit("subarea", sub)} size="large" disabled={actionLoading}>
                              <EditIcon />
                            </IconButton>
                            <IconButton edge="end" onClick={() => handleDeleteSubarea(sub.id)} size="large" disabled={actionLoading}>
                              <DeleteIcon />
                            </IconButton>
                            <IconButton edge="end" onClick={() => handleToggleSubarea(sub.id)} size="large">
                              {openSubareaIds.includes(sub.id) ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>

                        <Collapse in={openSubareaIds.includes(sub.id)} timeout="auto" unmountOnExit>
                          <Box pl={4}>
                            <Typography variant="body2">Especialidades</Typography>
                            {loadingSpecialties[String(sub.id)] ? (
                              <Box display="flex" alignItems="center" p={1}>
                                <CircularProgress size={16} />
                              </Box>
                            ) : (
                              <List>
                                {(sub.specialties || []).map((spec: any) => (
                                  <ListItem key={spec.id}>
                                    <ListItemText primary={spec.name} />
                                    <ListItemSecondaryAction>
                                      <IconButton edge="end" onClick={() => openEdit("specialty", spec)} size="large" disabled={actionLoading}>
                                        <EditIcon />
                                      </IconButton>
                                      <IconButton edge="end" onClick={() => handleDeleteSpecialty(spec.id)} size="large" disabled={actionLoading}>
                                        <DeleteIcon />
                                      </IconButton>
                                    </ListItemSecondaryAction>
                                  </ListItem>
                                ))}
                              </List>
                            )}
                          </Box>
                        </Collapse>
                      </Box>
                    ))}
                  </List>
                </Box>
              </Collapse>
            </Box>
          ))}
        </List>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth>
        <DialogTitle>{editing ? "Editar" : "Novo"}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nome"
            fullWidth
            value={nameValue}
            onChange={(e) => setNameValue(e.target.value)}
            disabled={actionLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={actionLoading}>
            Cancelar
          </Button>
          <Button onClick={submitDialog} variant="contained" disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={18} /> : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
